/**
 * useSurveySession — sesión individual con refresh manual
 *
 * @remarks
 * Carga una sesión por ID y expone reload para polling de estado.
 * El componente consume este hook cuando necesita refrescar status
 * sin recargar toda la lista.
 *
 * @module survey/hooks
 */

import { useCallback, useEffect, useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';
import type { SurveySession } from '../types/survey.types';

interface UseSurveySessionOptions {
  sessionId: string | null;
}

export interface UseSurveySessionResult {
  session: SurveySession | null;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useSurveySession({ sessionId }: UseSurveySessionOptions): UseSurveySessionResult {
  const [session, setSession] = useState<SurveySession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!sessionId) {
      setSession(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await surveyApi.getSession(sessionId);
      setSession(data);
    } catch (err) {
      setError(getSurveyApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { session, isLoading, error, reload: load };
}
